import { NavLink, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import {
  LayoutDashboard, FileText, Users, Package, Wallet, BarChart3, Settings as SettingsIcon,
  LogOut, Plus, Menu, X, ReceiptText, ShieldCheck, Boxes, Users2, CreditCard, ChevronsUpDown,
  Truck, ShoppingCart, Landmark, FileSignature, PackageCheck,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import AnnouncementBanner from "@/components/AnnouncementBanner";
import InstallApp from "@/components/InstallApp";

// `module` gates the link on the org's plan; entries without one always show.
const NAV = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard" },
  { to: "/invoices", label: "Invoices", icon: FileText, testid: "nav-invoices" },
  { to: "/quotations", label: "Quotations", icon: FileSignature, testid: "nav-quotations" },
  { to: "/challans", label: "Challans", icon: PackageCheck, testid: "nav-challans" },
  { to: "/credit-notes", label: "Credit Notes", icon: ReceiptText, testid: "nav-credit-notes", module: "credit_notes" },
  { to: "/payments", label: "Payments", icon: Wallet, testid: "nav-payments", module: "payments" },
  { to: "/customers", label: "Customers", icon: Users, testid: "nav-customers" },
  { to: "/suppliers", label: "Suppliers", icon: Truck, testid: "nav-suppliers" },
  { to: "/purchases", label: "Purchases", icon: ShoppingCart, testid: "nav-purchases", module: "purchases" },
  { to: "/banking", label: "Banking", icon: Landmark, testid: "nav-banking", module: "banking" },
  { to: "/items", label: "Items", icon: Package, testid: "nav-items" },
  { to: "/inventory", label: "Inventory", icon: Boxes, testid: "nav-inventory", module: "inventory" },
  { to: "/reports", label: "Reports", icon: BarChart3, testid: "nav-reports", module: "reports" },
  { to: "/team", label: "Team", icon: Users2, testid: "nav-team", module: "multi_user" },
  { to: "/billing", label: "Billing", icon: CreditCard, testid: "nav-billing" },
  { to: "/settings", label: "Settings", icon: SettingsIcon, testid: "nav-settings" },
];

const ROLE_LABEL = { OWNER: "Owner", STAFF: "Staff", VIEWER: "CA / Viewer" };

export const AppShell = ({ children }) => {
  const { user, organization, role, logout, modules, organizations } = useAuth();
  const [orgOpen, setOrgOpen] = useState(false);

  // A switch changes which tenant every request resolves to, so reload rather
  // than try to invalidate every cached view in place.
  const switchOrg = async (organization_id) => {
    try {
      await api.post("/auth/switch-organization", { organization_id });
      window.location.assign("/dashboard");
    } catch (e) {
      toast.error(apiError(e));
    }
  };
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const handler = (e) => {
      if (e.altKey && e.key.toLowerCase() === "i") {
        e.preventDefault();
        navigate("/invoices/new");
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [navigate]);

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900 flex">
      <aside
        data-testid="app-sidebar"
        className={`fixed z-40 inset-y-0 left-0 w-[248px] bg-slate-900 text-slate-300 flex flex-col transition-transform duration-200 lg:translate-x-0 ${
          open ? "translate-x-0" : "-translate-x-full"
        }`}
      >
        <div className="px-5 py-5 border-b border-slate-800">
          <div className="flex items-center justify-between">
            <div>
              <div className="font-heading text-lg font-bold tracking-tight text-white">ERP4U</div>
              <div className="text-[11px] uppercase tracking-[0.18em] text-blue-400">Billing · GST</div>
            </div>
            <button className="lg:hidden text-slate-400" onClick={() => setOpen(false)} data-testid="sidebar-close">
              <X size={18} />
            </button>
          </div>
        </div>

        <nav className="flex-1 overflow-y-auto py-3 px-2 space-y-0.5">
          {NAV.filter((n) => !n.module || (modules || []).includes(n.module))
            .map(({ to, label, icon: Icon, testid }) => (
            <NavLink
              key={to}
              to={to}
              data-testid={testid}
              onClick={() => setOpen(false)}
              className={({ isActive }) =>
                `flex items-center gap-3 rounded-md px-3 py-2 text-sm transition-colors duration-150 ${
                  isActive ? "bg-blue-600 text-white font-medium" : "hover:bg-slate-800 hover:text-white"
                }`
              }
            >
              <Icon size={16} strokeWidth={2} />
              {label}
            </NavLink>
          ))}
        </nav>

        <div className="border-t border-slate-800 p-4 space-y-3">
          {(organizations || []).length > 1 ? (
            <div className="relative">
              <button
                onClick={() => setOrgOpen((v) => !v)}
                data-testid="org-switcher"
                className="flex w-full items-center justify-between gap-2 rounded-md px-2 py-1.5 text-left transition-colors duration-150 hover:bg-slate-800"
              >
                <span className="min-w-0">
                  <span className="block truncate text-sm font-medium text-white">
                    {organization?.name}
                  </span>
                  <span className="block font-mono text-[11px] text-slate-400">
                    {organization?.gstin || "Unregistered"}
                  </span>
                </span>
                <ChevronsUpDown size={14} className="shrink-0 text-slate-400" />
              </button>
              {orgOpen && (
                <div className="absolute bottom-full left-0 z-50 mb-1 w-full overflow-hidden rounded-md border border-slate-700 bg-slate-800 shadow-lg"
                  data-testid="org-switcher-menu">
                  {organizations.map((o) => (
                    <button
                      key={o.id}
                      disabled={o.is_current}
                      onClick={() => switchOrg(o.id)}
                      data-testid={`switch-org-${o.id}`}
                      className={`flex w-full items-center justify-between gap-2 px-3 py-2 text-left text-xs transition-colors duration-150 ${
                        o.is_current ? "bg-blue-600 text-white" : "text-slate-300 hover:bg-slate-700"
                      }`}
                    >
                      <span className="truncate">{o.name}</span>
                      <span className="shrink-0 text-[10px] uppercase opacity-70">{o.role}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <div>
              <div className="text-sm font-medium text-white truncate" data-testid="sidebar-org-name">
                {organization?.name}
              </div>
              <div className="font-mono text-[11px] text-slate-400" data-testid="sidebar-org-gstin">
                {organization?.gstin || "Unregistered"}
              </div>
            </div>
          )}
          <div className="flex items-center justify-between gap-2">
            <div className="min-w-0">
              <div className="text-xs text-slate-300 truncate">{user?.name}</div>
              <div className="flex items-center gap-1 text-[11px] text-blue-400">
                <ShieldCheck size={11} /> {ROLE_LABEL[role] || role}
              </div>
            </div>
            <button
              onClick={logout}
              data-testid="logout-btn"
              className="text-slate-400 hover:text-rose-400 transition-colors duration-150"
              title="Sign out"
            >
              <LogOut size={16} />
            </button>
          </div>
        </div>
      </aside>

      {open && <div className="fixed inset-0 z-30 bg-slate-900/50 lg:hidden" onClick={() => setOpen(false)} />}

      <div className="flex-1 lg:ml-[248px] min-w-0">
        <header className="sticky top-0 z-20 flex items-center justify-between gap-3 border-b border-slate-200 bg-white/95 backdrop-blur px-4 sm:px-6 py-3">
          <button className="lg:hidden text-slate-600" onClick={() => setOpen(true)} data-testid="sidebar-open">
            <Menu size={20} />
          </button>
          <div className="hidden sm:block text-xs text-slate-500">
            FY {new Date().getMonth() + 1 >= 4 ? new Date().getFullYear() : new Date().getFullYear() - 1}
            -{String((new Date().getMonth() + 1 >= 4 ? new Date().getFullYear() : new Date().getFullYear() - 1) + 1).slice(-2)}
            <span className="mx-2 text-slate-300">|</span>
            <kbd className="rounded border border-slate-300 bg-slate-100 px-1.5 py-0.5 font-mono text-[10px]">Alt</kbd>
            <span className="mx-0.5">+</span>
            <kbd className="rounded border border-slate-300 bg-slate-100 px-1.5 py-0.5 font-mono text-[10px]">I</kbd>
            <span className="ml-1.5">new invoice</span>
          </div>
          <Button
            size="sm"
            data-testid="topbar-new-invoice"
            onClick={() => navigate("/invoices/new")}
            className="bg-blue-600 hover:bg-blue-700 text-white gap-1.5 rounded-full px-4"
          >
            <Plus size={15} /> New Invoice
          </Button>
        </header>
        <main className="p-4 sm:p-6 lg:p-8">
          <AnnouncementBanner />
          <div className="mt-4 first:mt-0">{children}</div>
        </main>
      </div>
      {/* Only ever offered to someone already signed in — asking a stranger
          on the login screen to install a billing app is noise. */}
      <InstallApp />
    </div>
  );
};

export default AppShell;
